import React, { useState, useMemo, useEffect } from "react";
import { usePDF } from "react-to-pdf";
import tw, { styled } from "twin.macro";
import { graphql } from "gatsby";
import _ from "lodash";
import moment from "moment-timezone";
import Layout from "../components/Layout";
import Header from "@shared/components/Header";
import Icon from "@shared/components/Icon";
import ScheduleList from "@shared/components/ScheduleList";
import Calendar from "@shared/components/Calendar";
import { TZ } from "@shared/constants";
import { Popover } from "react-tiny-popover";

import {
  getStats,
  addNumberSuffix,
  hasPassed,
  getLocationOptions,
  getLeagueOptions,
  getOpponentOptions,
  getFilteredEventsAndGamesByDate,
  getFilteredEventsAndGames,
} from "@shared/utils";
import ScheduleFilters from "@shared/components/ScheduleFilters";

import { useBreakpoint } from "gatsby-plugin-breakpoints";
import { HOME_TEAMS, MAIN_COLOR, SITE } from "../config";
import { getNextGame } from "@shared/utils";

const Container = styled.div`
  ${tw`w-full flex flex-col p-5`}
`;

const TopBar = styled.div`
  ${tw`w-full flex flex-col sm:flex-row items-center sm:justify-between sticky py-4 text-xl left-0`}
`;

const ActionContainer = styled.div`
  ${tw`flex justify-center items-center`}
`;
const ButtonContainer = styled.div`
  ${tw`flex mt-4 sm:my-0 [align-self: flex-end]`}
  ${tw`sm:flex `}
`;
const DateContainer = styled.div`
  ${tw`text-white py-0.5 px-4 border-[1px] border-gray-300 bg-[#EC1F27] text-xl [line-height: 2em] min-w-[160px] text-center`}
`;

const ToggleButton = styled.button`
  ${tw`border border-gray-300 bg-[#EC1F27] h-9 px-3 flex justify-center items-center`}

  &:disabled {
    opacity: 0.3;
  }
  & > .material-icons {
    ${tw`text-2xl text-white`}
  }
`;

const ActionButton = styled.button`
  ${tw`mx-1 sm:mx-2 flex items-center border border-gray-200 sm:px-2 sm:py-1 rounded not-disabled:hover:bg-gray-200 `}
  &:disabled > img {
    opacity: 0.2;
  }
  &:disabled > span {
    opacity: 0.2;
  }
  & > span {
    ${tw`ml-0.5 font-bold text-sm sm:text-base hidden sm:inline`}
  }
`;

const Svg = styled.img`
  ${tw`w-4 h-4`}
`;

const ActionSvg = styled.img`
  ${tw`w-8 h-8`}
`;

const LiveStreamContainer = styled.div`
  ${tw`flex flex-col items-center mt-4`}
`;
const LiveStreamText = styled.div`
  ${tw`text-2xl sm:text-3xl my-1 text-black/70 text-center`}
`;

const LiveButtonContainer = styled.a`
  ${tw`bg-[#EC1F27] text-white flex items-center px-4 py-0.5 text-sm font-bold hover:not-disabled:opacity-80 transition-opacity rounded my-4`}
  & > .material-icons {
    ${tw`mr-2 text-4xl`}
  }
`;
const LinkContainer = styled.div`
  ${tw`flex items-center`}
`;

const BoxScoreLink = styled.a`
  ${tw`bg-[#EC1F27] text-white flex items-center mx-1 px-4 py-0.5 text-sm font-bold hover:not-disabled:opacity-80 transition-opacity rounded my-4`}
  & > .material-icons {
    ${tw`text-4xl`}
  }
`;

const Record = styled.div`
  ${tw`flex justify-center`}
`;

const RecordItem = styled.div`
  ${tw`p-2 text-2xl sm:text-4xl font-bold`}
`;
const FilterButton = styled.button`
  ${tw`border border-gray-300 text-xs font-bold text-black rounded sm:mb-2 sm:ml-auto mr-1 flex items-center justify-center`}

  & > .material-icons {
    ${tw`text-[32px]`}
  }
`;

const FloLogo = styled.a`
  ${tw`ml-2 p-2 rounded hover:not-disabled:opacity-80 transition-opacity`}

  img {
    height: auto;
    width: 140px;
  }
`;

const ScheduleHeader = styled.div`
  ${tw`flex justify-between items-center my-2`}
`;

const DownloadButton = styled.button`
  ${tw`px-4 py-2 text-white font-bold rounded uppercase text-sm transition-all hover:opacity-90 ml-auto`}
  background-color: #ec1f27;
  border: none;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const CalendarSyncButton = styled.button`
  ${tw`px-4 py-2 mx-2 text-white font-bold rounded uppercase text-sm transition-all hover:opacity-90 ml-2 flex items-center`}
  background-color: #ec1f27;
  border: none;
  cursor: pointer;

  & > .material-icons {
    ${tw`mr-1 text-base`}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  ${tw`flex ml-auto`}
`;

const ScheduleContentWrapper = styled.div`
  ${tw`w-full`}
`;

const CalendarDropdown = styled.div`
  ${tw`relative`}
`;

const CalendarMenu = styled.div`
  ${tw`absolute right-0 mt-2 w-48 rounded-lg shadow-xl bg-white ring-1 ring-black ring-opacity-5 z-50 p-4`}
  display: ${(props) => (props.show ? "block" : "none")};
`;

const CalendarOption = styled.a`
  ${tw`flex items-center w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md transition-colors no-underline`}
`;

const Modal = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50`}
`;

const ModalContent = styled.div`
  ${tw`bg-white rounded-lg p-6 max-w-md mx-4`}
`;

const ModalTitle = styled.h3`
  ${tw`text-lg font-bold mb-4`}
`;

const ModalText = styled.p`
  ${tw`text-sm mb-4`}
`;

const InstructionList = styled.ol`
  ${tw`text-sm space-y-2 mb-4`}
`;

const InstructionItem = styled.li`
  ${tw`ml-4`}
`;

const ModalNote = styled.p`
  ${tw`text-xs text-gray-500 mb-4`}
`;

const ModalButton = styled.button`
  ${tw`bg-[#EC1F27] text-white px-4 py-2 rounded hover:bg-opacity-80 transition-opacity`}
`;

const SchedulePage = (props) => {
  const {
    data: {
      markdownRemark: {
        frontmatter: {
          seo,
          title,
          homeGameColor,
          awayGameColor,
          promoGameColor,
        },
      },
      allGames,
      allTeams,
      allEvents,
      homepage,
    },
    pageContext,
    location,
  } = props;

  const allGameNodes = allGames.edges;
  const allEventNodes = allEvents.edges;
  const pageYear = pageContext.year;

  const defaultDate = getDefaultDate(allGameNodes, pageYear, TZ);
  const currentLiveGame = allGameNodes.find((game) => {
    const timePassed = moment
      .tz(TZ)
      .diff(game.node.frontmatter.date, "minutes");
    return timePassed <= 180 && timePassed >= 0;
  });

  const nextGame = getNextGame(allGameNodes.map((item) => item.node));
  const teamsByName = _.keyBy(allTeams.edges, "node.frontmatter.name");
  const breakpoints = useBreakpoint();
  const isMobile =
    Object.values(breakpoints).reduce((a, b) => (b ? a + 1 : a), 0) === 0;
  const [view, setView] = useState(isMobile ? "list" : "calendar");
  // in ISO format
  const [currentDate, setCurrentDate] = useState(defaultDate);

  const [showCalendarMenu, setShowCalendarMenu] = useState(false);

  const [showOutlookModal, setShowOutlookModal] = useState(false);

  // game filters
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [filters, setFilters] = useState({
    weekday: null,
    homeAway: null,
    opponent: null,
    // promo: null,
    location: null,
    league: null,
  });

  const momentCurrDate = moment.tz(currentDate, TZ);

  const { toPDF, targetRef } = usePDF({
    filename: `${
      view === "calendar" ? "calendar" : "schedule"
    }-${momentCurrDate.format("MMMM-YYYY")}.pdf`,
    page: {
      format: "a4",
      orientation: "landscape",
      margin: 10,
    },
    canvasOptions: {
      scale: 1,
      useCORS: true,
      logging: false,
      backgroundColor: null,
    },
    overrides: {
      // Override canvas creation to add print-mode class
      canvas: {
        onclone: (clonedDoc) => {
          const element = clonedDoc.querySelector("[data-pdf-content]");
          if (element) {
            element.classList.add("print-mode");
          }
        },
      },
    },
  });

  const handleDownloadPDF = async () => {
    try {
      // Add print-mode class before generating
      targetRef.current?.classList.add("print-mode");

      // Small delay to let styles apply
      await new Promise((resolve) => setTimeout(resolve, 100));

      // Generate PDF
      await toPDF();

      // Remove print-mode class after generation
      targetRef.current?.classList.remove("print-mode");
    } catch (error) {
      console.error("PDF generation error:", error);
      targetRef.current?.classList.remove("print-mode");
    }
  };

  const handleOutlookClick = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText(getSubscribeLinks().outlook);
    setShowOutlookModal(true);
  };

  // this will not work locally
  const getSubscribeLinks = () => {
    if (typeof window === "undefined") return {};

    const host = window.location.host;
    const googleIcsUrl = `http://${host}/dawgs-schedule.ics`;
    const outlookIcsUrl = `https://${host}/dawgs-schedule.ics`;

    return {
      google: `https://calendar.google.com/calendar/r?cid=${encodeURIComponent(
        googleIcsUrl
      )}`,
      outlook: outlookIcsUrl,
    };
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showCalendarMenu && !e.target.closest(".calendar-dropdown")) {
        setShowCalendarMenu(false);
      }
    };

    if (showCalendarMenu) {
      document.addEventListener("click", handleClickOutside);
      return () => document.removeEventListener("click", handleClickOutside);
    }
  }, [showCalendarMenu]);

  const { dateFilteredGames, dateFilteredEvents } =
    getFilteredEventsAndGamesByDate(
      allGameNodes,
      allEventNodes,
      currentDate,
      TZ
    );

  const locationOptions = getLocationOptions(dateFilteredGames, teamsByName);
  const leagueOptions = getLeagueOptions(dateFilteredGames);

  const resetFilters = () => {
    setFilters({
      weekday: null,
      homeAway: null,
      opponent: null,
      // promo: null,
      location: null,
      league: null,
    });
  };

  const handleChangeMonth = (increment) => {
    const newDate = moment
      .tz(currentDate, TZ)
      .add(increment, "month")
      .toISOString();
    setCurrentDate(newDate);
  };
  const record = useMemo(
    () => getTeamRecord(allGameNodes, "Dawgs"),
    [allGameNodes]
  );

  const watchLiveLink = homepage.frontmatter.hero.watchLiveLink;
  const teamSeasonLink = homepage.frontmatter.hero.teamSeasonLink;
  const liveScoreLink = _.get(
    currentLiveGame,
    "node.frontmatter.liveScoreLink"
  );
  const tv =
    _.get(currentLiveGame, "node.frontmatter.tv") ||
    _.get(nextGame, "frontmatter.tv");

  addNumberSuffix(allGameNodes, true);

  const opponentOptions = getOpponentOptions(dateFilteredGames, HOME_TEAMS);

  let { filteredGames, filteredEvents, filteredGamesByTeamDate } =
    getFilteredEventsAndGames(
      dateFilteredGames,
      dateFilteredEvents,
      filters,
      currentDate,
      TZ,
      teamsByName,
      HOME_TEAMS
    );
  // combine and sort events/games
  const eventAndGames = _.sortBy(
    [...filteredGames, ...filteredEvents],
    (item) => {
      const { mdType, startDate, date } = item.node.frontmatter;
      if (mdType === "event") {
        return startDate;
      } else {
        return date;
      }
    }
  );
  const hasFilter =
    Object.values(filters).filter((value) => value !== null).length !== 0;

  const Filters = (
    <ScheduleFilters
      opponentOptions={opponentOptions}
      locationOptions={locationOptions}
      leagueOptions={leagueOptions}
      filters={filters}
      setFilters={setFilters}
      resetFilters={resetFilters}
      hasFilter={hasFilter}
      color={MAIN_COLOR}
      site={SITE}
    />
  );
  return (
    <Layout
      seoTitle={seo?.title || title}
      seoDescription={seo?.description}
      location={location}
    >
      <Container>
        <Header
          title={`${momentCurrDate.format("YYYY")} Schedule`}
          color={MAIN_COLOR}
          site={SITE}
        />
        <LiveStreamContainer>
          <LiveStreamText>Live Stream!</LiveStreamText>
          <LiveStreamText>Webcast available for all games.</LiveStreamText>
          <LinkContainer>
            {SITE === "dawgs" ? (
              <FloLogo
                href={tv || teamSeasonLink}
                target="_blank"
                rel="noopener noreferrer"
                disabled={_.isEmpty(tv) && _.isEmpty(teamSeasonLink)}
              >
                <img
                  src="/img/Black_1College.png"
                  alt="FloCollege"
                  loading="lazy"
                />
              </FloLogo>
            ) : (
              <LiveButtonContainer
                href={tv || teamSeasonLink}
                target="_blank"
                disabled={_.isEmpty(tv) && _.isEmpty(teamSeasonLink)}
              >
                <Icon name="videocam" /> <div>WATCH NOW</div>
              </LiveButtonContainer>
            )}
            {!_.isNil(currentLiveGame) && (
              <BoxScoreLink
                href={liveScoreLink || teamSeasonLink}
                target="_blank"
              >
                <Icon name="view_module" />
              </BoxScoreLink>
            )}
          </LinkContainer>
        </LiveStreamContainer>

        <Record>
          <RecordItem>Win: {record.win}</RecordItem>
          <RecordItem>Loss: {record.lose}</RecordItem>
        </Record>

        <TopBar>
          <ActionContainer>
            <ToggleButton
              onClick={() => handleChangeMonth(-1)}
              disabled={momentCurrDate.month() === 0}
            >
              <Icon name="arrow_left" />
            </ToggleButton>
            <DateContainer>{momentCurrDate.format("MMMM YYYY")}</DateContainer>
            <ToggleButton
              onClick={() => handleChangeMonth(1)}
              disabled={momentCurrDate.month() === 11}
            >
              <Icon name="arrow_right" />
            </ToggleButton>
          </ActionContainer>
          {!isMobile && (
            <ButtonContainer>
              <ActionButton
                onClick={() => setView("list")}
                disabled={view === "list"}
              >
                <ActionSvg src="/img/svg/list.svg" alt="list_view" />
                <span>List</span>
              </ActionButton>
              <ActionButton
                onClick={() => setView("calendar")}
                disabled={view === "calendar"}
              >
                <ActionSvg src="/img/svg/calendar.svg" alt="calendar_view" />
                <span>Calendar</span>
              </ActionButton>
            </ButtonContainer>
          )}
        </TopBar>

        {isMobile ? (
          <>
            <ButtonContainer>
              <Popover
                isOpen={mobileFilterOpen}
                content={Filters}
                padding={8}
                boundaryInset={16}
                positions={["bottom"]}
                onClickOutside={() => setMobileFilterOpen(false)}
              >
                <FilterButton
                  onClick={() => setMobileFilterOpen(!mobileFilterOpen)}
                >
                  <Icon name="tune" />
                </FilterButton>
              </Popover>
              <ActionButton
                onClick={() => setView("list")}
                disabled={view === "list"}
              >
                <ActionSvg src="/img/svg/list.svg" alt="list_view" />
                <span>List</span>
              </ActionButton>
              <ActionButton
                onClick={() => setView("calendar")}
                disabled={view === "calendar"}
              >
                <ActionSvg src="/img/svg/calendar.svg" alt="calendar_view" />
                <span>Calendar</span>
              </ActionButton>
            </ButtonContainer>
          </>
        ) : (
          Filters
        )}

        <ScheduleHeader>
          <ButtonGroup>
            <CalendarDropdown className="calendar-dropdown">
              <CalendarSyncButton
                onClick={() => setShowCalendarMenu(!showCalendarMenu)}
              >
                <Icon name="sync" />
                Sync Calendar
                <Icon name={showCalendarMenu ? "expand_less" : "expand_more"} />
              </CalendarSyncButton>

              <CalendarMenu show={showCalendarMenu}>
                <CalendarOption
                  href={getSubscribeLinks().google}
                  target="_blank"
                >
                  Google
                </CalendarOption>
                <CalendarOption
                  as="button"
                  onClick={handleOutlookClick}
                  style={{
                    cursor: "pointer",
                    width: "100%",
                    border: "none",
                    background: "transparent",
                  }}
                >
                  Outlook
                </CalendarOption>

                {showOutlookModal && (
                  <Modal onClick={() => setShowOutlookModal(false)}>
                    <ModalContent onClick={(e) => e.stopPropagation()}>
                      <ModalTitle>✓ Link Copied!</ModalTitle>
                      <ModalText>To add to Outlook:</ModalText>
                      <InstructionList>
                        <InstructionItem>
                          Open Outlook (web or desktop)
                        </InstructionItem>
                        <InstructionItem>
                          Go to Calendar → Add Calendar
                        </InstructionItem>
                        <InstructionItem>
                          Choose "Subscribe from web"
                        </InstructionItem>
                        <InstructionItem>Paste the copied link</InstructionItem>
                      </InstructionList>
                      <ModalNote>
                        Note: It may take a moment for events to appear
                      </ModalNote>
                      <ModalButton onClick={() => setShowOutlookModal(false)}>
                        Got it!
                      </ModalButton>
                    </ModalContent>
                  </Modal>
                )}
              </CalendarMenu>
            </CalendarDropdown>

            <DownloadButton onClick={handleDownloadPDF}>
              Download PDF
            </DownloadButton>
          </ButtonGroup>
        </ScheduleHeader>

        <ScheduleContentWrapper ref={targetRef}>
          {view === "list" && (
            <ScheduleList
              eventAndGames={eventAndGames}
              teamsByName={teamsByName}
              homeGameColor={homeGameColor}
              awayGameColor={awayGameColor}
              promoGameColor={promoGameColor}
              timezone={TZ}
              site={SITE}
              color={MAIN_COLOR}
              homeTeams={HOME_TEAMS}
              currentMonth={momentCurrDate.format("MMMM")}
            />
          )}
          {view === "calendar" && (
            <Calendar
              currentDate={currentDate}
              games={filteredGames}
              events={filteredEvents}
              teamsByName={teamsByName}
              homeGameColor={homeGameColor}
              awayGameColor={awayGameColor}
              promoGameColor={promoGameColor}
              timezone={TZ}
              site={SITE}
              homeTeams={HOME_TEAMS}
            />
          )}
        </ScheduleContentWrapper>
      </Container>
    </Layout>
  );
};

export default SchedulePage;

export const pageQuery = graphql`
  query SchedulePageQuery($year: Int) {
    markdownRemark(frontmatter: { templateKey: { eq: "schedule-page" } }) {
      frontmatter {
        seo {
          title
          description
        }
        title
        homeGameColor
        awayGameColor
        promoGameColor
      }
    }
    allGames: allMarkdownRemark(
      filter: {
        frontmatter: { templateKey: { eq: "game-post" }, yr: { eq: $year } }
      }
      sort: { order: ASC, fields: frontmatter___date }
    ) {
      edges {
        node {
          frontmatter {
            yr
            date
            homeTeam
            awayTeam
            showBuyTicket
            ticketLink
            liveScoreLink
            promoGame
            promoDescriptions
            bgColor
            tv
            radio
            stats {
              inningStats {
                home
                away
              }
            }
          }
          id
          html
        }
      }
    }
    allTeams: allMarkdownRemark(
      filter: { frontmatter: { mdType: { eq: "team" } } }
    ) {
      edges {
        node {
          frontmatter {
            name
            logo {
              childImageSharp {
                gatsbyImageData(height: 75)
              }
            }
            location {
              fieldName
              abbreviation
              town
              address
            }
          }
        }
      }
    }
    allEvents: allMarkdownRemark(
      filter: { frontmatter: { mdType: { eq: "event" } } }
      sort: { order: ASC, fields: frontmatter___startDate }
    ) {
      edges {
        node {
          frontmatter {
            mdType
            name
            startDate
            endDate
            highlightColor
          }
        }
      }
    }
    homepage: markdownRemark(
      frontmatter: { templateKey: { eq: "index-page" } }
    ) {
      frontmatter {
        hero {
          watchLiveLink
          teamSeasonLink
        }
      }
    }
  }
`;

const getDefaultDate = (games, year, timezone) => {
  const currentDate = moment().tz(timezone).toISOString();
  const upcomingGamesForCurrentYear = games.filter((game) => {
    const isCurrentYear =
      moment.tz(game.node.frontmatter.date, timezone).year() === parseInt(year);
    const isFutureGame = game.node.frontmatter.date > currentDate;
    return isCurrentYear && isFutureGame;
  });

  const mostRecentUpcomingGameDate = _.get(
    upcomingGamesForCurrentYear,
    "0.node.frontmatter.date"
  );
  if (mostRecentUpcomingGameDate) {
    const date = moment(mostRecentUpcomingGameDate)
      .tz(timezone)
      .startOf("month")
      .toISOString();
    return date;
  } else {
    return parseInt(year) === moment.tz(timezone).year()
      ? moment.tz(timezone).toISOString()
      : moment
          .tz(timezone)
          .year(parseInt(year))
          .month(0)
          .startOf("month")
          .toISOString();
  }
};

const getTeamRecord = (allGames, team) => {
  let record = { total: 0, win: 0, lose: 0 };
  allGames.forEach((obj) => {
    const { awayTeam, homeTeam, stats } = obj.node.frontmatter;
    const gameForTeam = [awayTeam, homeTeam].includes(team);
    const homeTeamInnings = _.get(stats, "inningStats.home");
    const awayTeamInnings = _.get(stats, "inningStats.away");
    if (
      !gameForTeam ||
      _.isEmpty(homeTeamInnings) ||
      _.isEmpty(awayTeamInnings)
    ) {
      return;
    }
    const { score } = getStats(stats.inningStats);

    const isHomeGameForTeam = homeTeam === team;
    const homeGameVictory = score.home > score.away;
    const awayGameVictory = score.away > score.home;
    // draw is not counted
    record.total += 1;
    if (isHomeGameForTeam) {
      record.win += homeGameVictory ? 1 : 0;
      record.lose += awayGameVictory ? 1 : 0;
    } else {
      record.win += awayGameVictory ? 1 : 0;
      record.lose += homeGameVictory ? 1 : 0;
    }
  });
  return record;
};
